import { fetchFromContentful } from "../../utils/contentful/api";
import gql from "graphql-tag";
import { Link } from "../../components/Actions";
import { generateSingleVideoSlug } from "../../utils/contentful/generateSlugs";
import { formatDate } from "../../utils/dateTime";
import { VideoGroup } from "../../components/VideoGroup";
import { PreviewWrapper } from "../../components/PreviewWrapper/PreviewWrapper";
import SEO, { deriveDataFromContentfulEntry } from "../../components/Seo/Seo";
import { groupVideosByCategory } from "../../utils/groupVideosByCategory";
import { TabbedVideoGroup } from "../../components/VideoGroup/Tabbed";

const WatchPage = ({ page, preview, groupedVideos }: any) => {
  const videoGroups: any[] = page.videoGroupsCollection.items;
  const desktopHeaderVideo = page.headerMediaCollection.items[0];
  const mobileHeaderVideo = page.headerMediaCollection.items[1];

  const episodeNumber = page.featuredVideo.episodeNumber;
  const seasonNumber = page.featuredVideo.seasonGroup.seasonNumber;
  const showEpisodeNumberInTitle =
    typeof episodeNumber === "number" && typeof seasonNumber === "number";

  return (
    <PreviewWrapper preview={preview}>
      <SEO {...deriveDataFromContentfulEntry(page.socialShareData)} />
      <div className="">
        {/* Desktop Media */}
        <div className="relative tab:mb-[60px] h-[calc(100vh-90px)] min-h-[700px] overflow-hidden">
          <video
            autoPlay
            loop
            muted
            controls={false}
            className="hidden tab:flex h-full w-full object-cover object-center"
            poster={desktopHeaderVideo.posterImage.url}>
            <source
              src={desktopHeaderVideo.loopingVideo.url}
              type={desktopHeaderVideo.loopingVideo.contentType}
            />
          </video>
          {/* Mobile Media */}

          <video
            autoPlay
            loop
            muted
            controls={false}
            className="tab:hidden w-full"
            poster={mobileHeaderVideo.posterImage.url}>
            <source
              src={mobileHeaderVideo.loopingVideo.url}
              type={mobileHeaderVideo.loopingVideo.contentType}
            />
          </video>

          <div className="absolute bottom-0 tab:bottom-0 w-full landing-gradient">
            <div className="w-full mx-auto flex flex-col tab:flex-row max-w-[1200px] px-4 mb-[60px] tab:mb-[100px] pt-40">
              <div className="tab:w-1/3">
                <h1 className="display1 mb-[15px] tab:mb-[5px]">Coinage</h1>
                <Link
                  modifier="primary"
                  analyticsProperty=""
                  className="hidden tab:flex"
                  href={generateSingleVideoSlug(
                    page.featuredVideo.seasonGroup.slug,
                    page.featuredVideo.urlSlug
                  )}>
                  {page.buttonText}
                </Link>
              </div>
              <div className="tab:w-[60%] ml-auto mt-auto">
                <h2 className="subhead2 mb-[15px] tab:mb-[5px] !font-bold">
                  {showEpisodeNumberInTitle && (
                    <>
                      S{seasonNumber} E{episodeNumber} •{" "}
                    </>
                  )}
                  {page.featuredVideo.articleTitle}
                </h2>
                <p className="body1 mb-[15px] tab:mb-[5px]">
                  {page.featuredVideo.articleDescription}
                </p>
                <p className="text-coinage-body body1">
                  {formatDate(page.featuredVideo.publishedDate)}
                </p>

                <Link
                  modifier="primary"
                  analyticsProperty=""
                  className="tab:hidden mt-[15px]"
                  href={generateSingleVideoSlug(
                    page.featuredVideo.seasonGroup.slug,
                    page.featuredVideo.urlSlug
                  )}>
                  {page.buttonText}
                </Link>
              </div>
            </div>
          </div>
        </div>
        {/* {videoGroups.map((videoGroup, idx) => ( */}
        <VideoGroup
          maxVideos={{ desktop: 3, mobile: 3 }}
          showBorderTop={false}
          key={videoGroups[0].title}
          title={videoGroups[0].title}
          subtitle={videoGroups[0].subtitle}
          showDescription={true}
          videos={videoGroups[0].videoArticlesCollection.items}
        />
        {/* ))} */}
        <TabbedVideoGroup
          categories={groupedVideos.categories}
          videos={groupedVideos.videos}
        />
      </div>
    </PreviewWrapper>
  );
};

export const getStaticProps = async ({ preview = false }: any) => {
  const slug = "watch";

  const query = gql`
    query SingleLanding($slug: String, $preview: Boolean) {
      allItems: videoArticleCollection(order: publishedDate_DESC) {
        items {
          articleTitle
          articleDescription
          shortArticleDescription
          seasonGroup {
            seasonNumber
            slug
          }
          urlSlug
          video {
            videoUpload
            fallbackImage {
              url
              width
              height
              description
            }
          }
          image {
            url
            width
            height
            description
          }
          category {
            categoryTitle
            orderInTab
          }
        }
      }
      seasonLandingPageCollection(
        where: { urlSlug: $slug }
        limit: 1
        preview: $preview
      ) {
        items {
          socialShareData {
            title
            excerpt
            shareImage {
              url
            }
          }

          videoGroupsCollection(limit: 5) {
            items {
              title
              subtitle
              showDescription
              videoArticlesCollection(limit: 20) {
                items {
                  category {
                    categoryTitle
                  }
                  urlSlug
                  seasonGroup {
                    slug
                  }
                  articleTitle
                  articleDescription
                  shortArticleDescription
                  episodeNumber
                  image {
                    url
                    width
                    height
                    description
                  }
                  video {
                    videoUpload
                    fallbackImage {
                      url
                      width
                      height
                      description
                    }
                  }
                }
              }
            }
          }
          headerMediaCollection(limit: 2) {
            items {
              posterImage {
                url
              }
              loopingVideo {
                url
                contentType
              }
            }
          }
          buttonText
          featuredVideo {
            urlSlug
            episodeNumber
            seasonGroup {
              seasonNumber
              slug
            }
            articleTitle
            articleDescription
            byline
            publishedDate
          }
        }
      }
    }
  `;

  const { data, errors } = await fetchFromContentful({
    query,
    variables: { slug },
    preview
  });

  if (errors) {
    console.error(errors);
  }

  const page = data.seasonLandingPageCollection.items[0];
  const groupedVideos = groupVideosByCategory(data.allItems.items);

  if (!page) {
    return {
      notFound: true
    };
  }

  return {
    props: { page, groupedVideos, preview },
    revalidate: 600
  };
};

export default WatchPage;
